import {computed, ComputedRef, Ref, ref} from "vue";
import {Survey} from "@/core/interfaces/survey/Survey";
import domHelper from "@/core/helpers/domHelper";

/**
 * Composable for managing survey form state, including user answers, validation, and submission.
 *
 * @param survey
 * @param debug If true, allows submission even if there are validation errors (useful for development/testing).
 */
const useSurveyForm = (survey: ComputedRef<Survey | undefined> | Ref<Survey | undefined>, debug: boolean = false) => {
    const userAnswers = ref<Record<number, (string | number | boolean | undefined)[]>>({});
    const dirty = ref(false);

    /**
     * The survey errors.
     */
    const errors = computed(() => {
        if (!survey.value?.sections || !dirty.value) {
            return {};
        }

        return survey.value.sections.map(section => section.questions)
            .flat()
            .reduce((acc, question) => {
                if (!question) {
                    return acc;
                }

                const answers = (userAnswers.value[question.id] || [])
                    .filter(value => value !== null && value !== undefined && value !== '');

                const isRequired = !question.optional;

                if (isRequired && answers.length === 0) {
                    acc[question.id] = !answers.length ? 'Ce champ est obligatoire' : undefined;
                }

                return acc;
            }, {} as Record<number, string | undefined>);
    });

    /**
     * Check if the survey has errors.
     */
    const surveyHasErrors = () => Object.keys(errors.value).some(key => errors.value[key] !== undefined);

    /**
     * Scroll to the first error.
     */
    const scrollToFirstError = (): void => {
        const ids = Object.keys(errors.value)
            .filter(key => !!errors.value[key]);

        if (ids.length > 0) {
            const firstErrorId = ids[0];
            domHelper.scrollIntoView(`question-${firstErrorId}`, {
                behavior: 'smooth',
                block: 'start',
                inline: 'start'
            });
        }
    };

    /**
     * Initialize the user answers based on the survey questions and their previous answers (if any).
     */
    const initAnswers = (survey: Survey) => {
        const questions = survey.sections?.map(section => section.questions)
            .flat() ?? [];

        userAnswers.value = questions.reduce((acc, question) => {
            if (!question) {
                return acc;
            }

            acc[question.id] = question.user_answer?.value ?? [];

            return acc;
        }, {});
    };

    /**
     * Handle the change event.
     *
     * @param {Object} value The new value.
     * @param {number} questionId The question ID.
     */
    const handleChange = ({value, questionId}) => {
        userAnswers.value[questionId] = Array.isArray(value) ? value : [value];
    };

    /**
     * Remove the empty answers from the answers object for debugging purposes to by pass the validation and submit the form with only the filled answers.
     */
    const debugAnswers = (answers: Record<number, (string | number | boolean | undefined)[]>) => {
        return Object.keys(answers)
            .filter((key) => answers[key]?.length > 0)
            .reduce((acc, key) => {
                acc[key] = answers[key];
                return acc;
            }, {} as Record<number, (string | number | boolean | undefined)[]>);
    };

    /**
     * Check for errors, and call the success callback.
     */
    const submit = (
        successCallback: (answers: Record<number, (string | number | boolean | undefined)[]>) => void
    ) => {
        dirty.value = true;

        if (surveyHasErrors() && !debug) {
            scrollToFirstError();
            return;
        }

        const answers = debug ? debugAnswers(userAnswers.value) : userAnswers.value;

        successCallback(answers);
    };

    return {
        initAnswers,
        handleChange,
        submit,
        errors,
        userAnswers,
        dirty
    };
};

export default useSurveyForm;
